import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'
import ChevronDownIcon from '@offerzen/zenicon/ChevronDownIcon'
import { colors } from '../../themes'
import Counter from '../Counter'
import FormErrorMessage from '../FormErrorMessage'
import FormHelpText from '../FormHelpText'
import AccessibleIcon from '../AccessibleIcon'
import styles, { wrapperTheme, labelTheme } from './styles'

const MultiSelect = ({ children, theme, meta, input, readOnly, helpText, ...props }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState(-1)
  const nodeRef = useRef()
  const buttonRef = useRef()
  const listBoxRef = useRef()

  const value = input.value || props.value || []
  const onBlur = input.onBlur || props.onBlur

  // Focus on listbox when open
  useEffect(() => {
    if (isOpen) {
      listBoxRef.current.focus()
    } else {
      setFocusIndex(-1)
    }
  }, [isOpen])

  useEffect(() => {
    if (props.autoFocus) buttonRef.current.focus()
  }, [props.autoFocus])

  // Close when clicking outside dropdown
  useEffect(() => {
    const handleClosingClick = ev => {
      if (nodeRef.current.contains(ev.target)) return

      setIsOpen(false)
      if (onBlur) onBlur(ev)
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClosingClick)
    } else {
      document.removeEventListener('mousedown', handleClosingClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClosingClick)
    }
  }, [isOpen, onBlur])

  const onLabelClick = ev => {
    setIsOpen(currentOpenValue => !currentOpenValue)

    const focusHandler = input.onFocus || props.onFocus
    if (!isOpen && focusHandler) focusHandler(ev)

    if (isOpen && onBlur) onBlur(ev)
  }

  const keyboardFocusIndexOnOpen = () => {
    if (value.length === 0) return 0

    const childrenArr = React.Children.toArray(children)
    if (childrenArr.length === 0) return 0

    const firstSelectedChildIndex = childrenArr.findIndex(child => (Array.isArray(value) ? value.includes(child.props.id) : -1))
    return firstSelectedChildIndex
  }

  const handleButtonNavigation = ev => {
    switch (ev.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
      case 'Enter':
        ev.preventDefault()

        if (!isOpen) {
          setIsOpen(true)
          const keyboardFocusIndex = keyboardFocusIndexOnOpen()
          setFocusIndex(keyboardFocusIndex)
        }

        break

      case 'Escape':
        ev.preventDefault()

        setIsOpen(false)

        break

      default:
        break
    }
  }

  const onChildChange = id => {
    const idIsInArray = Array.isArray(value) ? value.includes(id) : false
    const newValue = idIsInArray ? value.filter(option => option !== id) : [...value, id]
    const name = input.name || props.name

    // Call the handlers
    if (input.onChange) input.onChange(newValue)
    if (props.onSelect) props.onSelect(name, newValue, value)
  }

  const handleOptionsNavigation = ev => {
    const keyboardFocusIndex = focusIndex < 0 ? 0 : focusIndex

    switch (ev.key) {
      case 'ArrowUp':
      case 'ArrowDown': {
        ev.preventDefault()

        const offsetDirection = ev.key === 'ArrowDown' ? 1 : -1
        const focusOffset = keyboardFocusIndex + offsetDirection

        if (focusOffset >= 0 && focusOffset < React.Children.count(children)) {
          setFocusIndex(focusOffset)

          // If the body is scrollable, make sure the selected option is visible
          // Also, we're making sure that the reference exists to avoid null errors
          const listBox = listBoxRef.current
          if (listBox && listBox.scrollHeight > listBox.offsetHeight) {
            // Get the reference of the new selected option
            const focussedOption = listBox.children[focusOffset]
            // Calculate the visible area of the menu
            const visibleArea = listBox.scrollTop + listBox.offsetHeight
            if (focussedOption.offsetTop < listBox.scrollTop) {
              focussedOption.scrollIntoView()
            } else if (focussedOption.offsetTop + focussedOption.offsetHeight > visibleArea) {
              // eslint-disable-next-line operator-assignment
              listBox.scrollTop = listBox.scrollTop + focussedOption.offsetHeight
            }
          }
        }

        break
      }

      case 'Escape': {
        ev.preventDefault()

        setIsOpen(false)
        buttonRef.current.focus()

        break
      }

      case 'Enter':
      case ' ': {
        ev.preventDefault()

        const selectedChild = React.Children.toArray(children)[keyboardFocusIndex]
        const childId = selectedChild.props.id

        if (childId) onChildChange(childId)

        break
      }

      default:
        break
    }
  }

  const getActiveDescendent = () => {
    const childrenArr = React.Children.toArray(children)
    if (!childrenArr.length) return

    if (focusIndex < 0) return

    const activeElementId = childrenArr[focusIndex].props.id

    // eslint-disable-next-line consistent-return
    return activeElementId || childrenArr[0].props.id
  }

  const activeDescendant = getActiveDescendent()

  const parentName = input.name || props.name
  const errored = meta.touched && meta.error
  const isPlaceholder = value.length === 0

  return (
    <div
      ref={nodeRef}
      className={cx(styles.wrapper, props.className, wrapperTheme(theme), errored && styles.wrapperError)}
      onBlur={event => {
        const focusedElement = event.relatedTarget
        if (nodeRef.current.contains(focusedElement)) return

        if (onBlur) onBlur(event)
        setIsOpen(false)
      }}>
      <div
        className={cx(styles.label, labelTheme(theme), isOpen && styles.openLabel, errored && styles.errorLabel)}
        readOnly={readOnly}
        tabIndex={readOnly ? '-1' : props.tabIndex}
        ref={buttonRef}
        onClick={onLabelClick}
        onKeyDown={handleButtonNavigation}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={`${props.ariaLabelledby || props.id || ''}`}>
        <div className={cx(styles.input, isPlaceholder && styles.placeholderInput)}>
          {value.length > 0 ? props.label : props.placeholder || props.label}
        </div>

        <div className={styles.arrow}>
          {Boolean(!!props.counterValue || value.length > 0) && (
            <Counter value={props.counterValue || value.length} theme={errored ? 'error' : theme} className={styles.counter} />
          )}

          <AccessibleIcon icon={ChevronDownIcon} size={18} color={errored ? colors.mandy : colors.geyser} />
        </div>
      </div>
      {Boolean(isOpen) && (
        <ul
          className={styles.body}
          role="listbox"
          aria-multiselectable="true"
          tabIndex="-1"
          aria-activedescendant={activeDescendant}
          ref={listBoxRef}
          onKeyDown={handleOptionsNavigation}>
          {React.Children.map(children, (child, index) =>
            React.cloneElement(child, {
              name: `${parentName}[${index}]`,
              onChange: () => onChildChange(child.props.id),
              theme,
              selected: Array.isArray(value) ? value.includes(child.props.id) : false,
              focused: focusIndex === index ? 'true' : null,
            }),
          )}
        </ul>
      )}
      {Boolean(helpText && !errored) && <FormHelpText text={helpText} />}
      {Boolean(errored) && <FormErrorMessage text={meta.error} />}
    </div>
  )
}

const { shape, string, number, bool, func, node, arrayOf, oneOf, oneOfType } = PropTypes
MultiSelect.propTypes = {
  meta: shape({
    touched: bool,
    error: string,
  }),
  input: shape({
    value: oneOfType([arrayOf(string), string]),
    onBlur: func,
    onFocus: func,
    onChange: func,
  }),
  value: oneOfType([arrayOf(string), string]),
  onBlur: func,
  onFocus: func,
  id: string,
  name: string,
  label: string.isRequired,
  placeholder: string,
  counterValue: number,
  helpText: string,
  readOnly: bool,
  onSelect: func,
  tabIndex: string,
  theme: oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'dark', 'tertiary', 'info']),
  className: string,
  children: oneOfType([arrayOf(node), node]),
  ariaLabelledby: string,
  autoFocus: bool,
}

MultiSelect.defaultProps = {
  input: {},
  meta: {},
  value: [],
  theme: 'primary',
  tabIndex: '0',
}

export default MultiSelect
